// src/services/push-notifications/app-push-notification-manager-web.ts

import {
  AppPushNotificationsProps,
  AppPushPermissionStatus,
} from '@common/core/capacitor/app-push-notifications/assets/app-push-notifications-interface';
import { getBootstrapData, mergeBootstrapData } from '@ui/bootstrap-data/bootstrap-data-store';
import { message } from '@ui/i18n/message';
import { toast } from '@ui/toast/toast';
import { Messaging } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';

export class AppPushNotificationsWeb implements AppPushNotificationsProps {
  
  // @ts-ignore
  private messaging: Messaging;
  private fcmToken: string | null = null;
  private tools: Record<string, any> = {};
  private firebase: Record<string, any> = {};
  private unsubscribeForegroundMessages?: () => void;
  private readonly STORAGE_KEY_TOKEN = 'pn-token';
  private readonly STORAGE_KEY_REQUESTED_PERMISSION = 'pn-requested-permission';
  private registration: ServiceWorkerRegistration | null = null;
  private loadPromise?: Promise<void>;
  public notificationIsSupported: boolean = false;

  constructor(tools: Record<string, any>) {
    this.tools = tools;
  }

  private async waitForServiceWorkerReady(timeoutMs = 4000): Promise<boolean> {
    if (!('serviceWorker' in navigator)) return false;

    try {
      await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('SW ready timeout')), timeoutMs)
        ),
      ]);
      return true;
    } catch (e) {
      console.warn('Service worker not ready (timeout). Continuing anyway.', e);
      return false;
    }
  }
  
  // public async loadManager(): Promise<void> {
  //   const { getToken, deleteToken, onMessage, isSupported } = await import('firebase/messaging');
    
  //   this.notificationIsSupported = await isSupported();
    
  //   if (!this.notificationIsSupported) {
  //     console.warn('Les notifications push ne sont pas supportées dans ce navigateur.');
  //   } else {
  //     const { messaging } = await import('@common/core/capacitor/app-push-notifications/assets/firebase-config');
  //     this.messaging = messaging;
  //     this.firebase = { getToken, deleteToken, onMessage, isSupported };
  //   }

  //   return Promise.resolve();
  // }

  public loadManager(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = (async () => {
      const { getToken, deleteToken, onMessage, isSupported } = await import('firebase/messaging');
      this.notificationIsSupported = await isSupported();
      if (!this.notificationIsSupported) return;

      const { messaging } = await import('@common/core/capacitor/app-push-notifications/assets/firebase-config');
      this.messaging = messaging;
      this.firebase = { getToken, deleteToken, onMessage, isSupported };
    })();

    return this.loadPromise;
  }

  /**
   * Initialiser la gestion des notifications push
   */
  public async init(): Promise<void> {
    await this.loadManager(); 
    if (!this.notificationIsSupported) return;
    
    const sw = await this.registerFirebaseServiceWorker();
    if (!sw) return;

    const permission = await this.hasPermission();
    if (!permission) return;

    const token = await this.getToken();
    if (!token) return;

    this.setRequestedPermission();
    this.fcmToken = token;

    const storedToken = localStorage.getItem(this.STORAGE_KEY_TOKEN);
    if (token !== storedToken) {
      await this.tools.storeToken(token);
      localStorage.setItem(this.STORAGE_KEY_TOKEN, token);
    }

    this.handleForegroundMessages();
  }

  /**
   * Remove le token FCM
   * - À appeler au logout (user déconnecté)
   */
  public async uninit(): Promise<void> {
    if (!this.notificationIsSupported) return;

    const token = this.fcmToken ?? localStorage.getItem(this.STORAGE_KEY_TOKEN);
    if (token) {
      // await deleteToken(this.messaging);
      // await this.tools.deleteToken(token);
      this.firebase.deleteToken(this.messaging);
      this.tools.deleteToken(token);
      this.setRequestedPermission(false);
      localStorage.removeItem(this.STORAGE_KEY_TOKEN);
      this.fcmToken = null;
    }

    if (this.unsubscribeForegroundMessages) {
      this.unsubscribeForegroundMessages();
      this.unsubscribeForegroundMessages = undefined;
    }
  }

  /**
   * Synchronise le token FCM avec la BDD
   * - À appeler au retour sur le site (user connecté)
   */
  public async syncToken(): Promise<void> {
    if (!this.notificationIsSupported) return;

    const permission = await this.hasPermission();
    if (!permission) return;

    const token = await this.getToken();
    if (!token) return;

    this.setRequestedPermission();
    this.fcmToken = token;

    const storedToken = localStorage.getItem(this.STORAGE_KEY_TOKEN);
    if (token !== storedToken) {
      await this.tools.storeToken(token);
      localStorage.setItem(this.STORAGE_KEY_TOKEN, token);
    }
  }

  /**
   * Enregistre le service worker pour les notifications push
   * @returns {Promise<ServiceWorkerRegistration | null>} L'enregistrement du service worker ou null en cas d'erreur
   */
  public async registerFirebaseServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (Capacitor.isNativePlatform() || !('serviceWorker' in navigator)) {
      console.warn('Service Worker non supporté dans ce navigateur.');
      return null;
    }
    
    try {
      this.registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await this.waitForServiceWorkerReady(4000); 
      return this.registration;
    } catch (error) {
      console.error('❌ Erreur lors de l\'enregistrement du Service Worker :', error);
      return null;
    }
  }

  /**
   * Demander la permission de notifications
   * @returns {Promise<boolean>} true si la permission est accordée, false sinon
   */
  public async requestPermission(): Promise<Record<string, any>> {
    if (!this.notificationIsSupported) return { granted: false, status: "unsupported" };
    const status = await Notification.requestPermission();
    await this.setRequestedPermission();
    return {
      granted: status === 'granted',
      status: await this.getPermissionStatus(),
    };
  }

  /**
   * Vérifie si l'application a déjà la permission d'envoyer des notifications
   * @returns {Promise<boolean|null|undefined>} true si la permission est accordée, false sinon
   */
  public async hasPermission(): Promise<boolean|null|undefined> {
    if (!this.notificationIsSupported) {
      console.warn('Les notifications push ne sont pas supportées dans ce navigateur.');
      return false;
    }
    
    return (await this.getPermissionStatus()) === 'granted';
  }

  public async getPermissionStatus(): Promise<AppPushPermissionStatus> {
    if (!this.notificationIsSupported) {
      return 'unsupported';
    }

    switch (Notification.permission) {
      case 'granted':
        return 'granted';
      case 'denied':
        return 'denied';
      case 'default':
        return 'default';
      default:
        return 'unknown';
    }
  }

  /**
   * Vérifie si la permission de notification a déjà été demandée
   * @returns {boolean} true si la permission a été demandée, false sinon
   */
  public async hasRequestedPermission(): Promise<boolean> {
    return localStorage.getItem(this.STORAGE_KEY_REQUESTED_PERMISSION) === 'true';
  }

  /**
   * Met à jour le localStorage pour indiquer que la permission a été demandée
   */
  public async setRequestedPermission(value: boolean = true): Promise<void> {
    localStorage.setItem(this.STORAGE_KEY_REQUESTED_PERMISSION, value.toString());
  }

  /**
   * Récupérer le token FCM
   * @returns {Promise<string | null>} Le token ou null en cas d'erreur
   */
  public async getToken(): Promise<string|null|undefined> {
    try {
      const vapidPublicKey = await this.tools.getVapidKey();
      const token = await this.firebase.getToken(this.messaging, { 
        vapidKey: vapidPublicKey,
        serviceWorkerRegistration: this.registration
      });
      return token || null;
    } catch (error) {
      console.error('Erreur lors de la récupération du token FCM:', error);
      return null;
    }
  }

  public async openConfig(): Promise<void> {
    await this.requestPermission();
    return Promise.resolve();
  }

  /**
   * Gérer les messages reçus quand l'application est au foreground
   */
  private handleForegroundMessages(): void {
    this.unsubscribeForegroundMessages = this.firebase.onMessage(this.messaging, async (payload: any) => {
      const newNotificationCount = await this.tools.getUnreadNotifications();
      const bootstrapData = getBootstrapData();
      const notificationTitle = payload.notification?.title || payload?.data?.title || 'Hey, check this out!';
      const notificatioAction = payload.data?.url 
        ? {action: payload.data?.url, label: payload.data?.cta || 'View details'}
        : undefined;

      if(newNotificationCount) {
        mergeBootstrapData({
            user: {...bootstrapData.user!, unread_notifications_count: newNotificationCount},
        });
      }

      // Afficher une notification avec un toast
      toast(
        message(notificationTitle),
        {
            type: 'notify',
            action: notificatioAction,
            duration: 5000,
            position: 'bottom-left',
        }
      );
    });
  }
}
